/**
 * useSectionTruncation - Hook for section truncation state
 *
 * Manages "Show more/less" state for list sections.
 * Used by IssuesSection, SuggestionsSection, etc.
 *
 * Phase 3.3: Mobile Results Optimization
 */

import { useState, useCallback, useMemo } from 'react';

export interface UseSectionTruncationOptions {
  /** Maximum items to show before truncation (default: 3) */
  maxVisible?: number;
  /** Total number of items in the list */
  totalItems: number;
}

export interface UseSectionTruncationReturn {
  /** Whether all items are shown */
  showAll: boolean;
  /** Toggle to show all items */
  expandAll: () => void;
  /** Toggle to collapse back to truncated view */
  collapseAll: () => void;
  /** Toggle between expanded/collapsed */
  toggle: () => void;
  /** Whether truncation is needed (totalItems > maxVisible) */
  isTruncated: boolean;
  /** Number of items currently visible */
  visibleCount: number;
  /** Number of hidden items */
  hiddenCount: number;
}

/**
 * Hook for managing section truncation state
 *
 * @example
 * const { showAll, toggle, isTruncated, visibleCount, hiddenCount } = useSectionTruncation({
 *   totalItems: issues.length,
 *   maxVisible: 3,
 * });
 *
 * const visibleIssues = showAll ? issues : issues.slice(0, visibleCount);
 */
export function useSectionTruncation({
  maxVisible = 3,
  totalItems,
}: UseSectionTruncationOptions): UseSectionTruncationReturn {
  const [showAll, setShowAll] = useState(false);

  const isTruncated = totalItems > maxVisible;
  const visibleCount = showAll || !isTruncated ? totalItems : maxVisible;
  const hiddenCount = isTruncated ? totalItems - maxVisible : 0;

  const expandAll = useCallback(() => {
    setShowAll(true);
  }, []);

  const collapseAll = useCallback(() => {
    setShowAll(false);
  }, []);

  const toggle = useCallback(() => {
    setShowAll((prev) => !prev);
  }, []);

  return useMemo(
    () => ({
      showAll,
      expandAll,
      collapseAll,
      toggle,
      isTruncated,
      visibleCount,
      hiddenCount,
    }),
    [showAll, expandAll, collapseAll, toggle, isTruncated, visibleCount, hiddenCount]
  );
}

/** Default truncation limits per section type */
export const TRUNCATION_DEFAULTS = {
  issues: 3,
  suggestions: 2,
  patterns: 3,
  transformations: 2,
} as const;
